import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Reveal, Eyebrow } from "./Reveal";

const IMAGES = [
  {
    src: "/photos/patio_pool.jpg",
    alt: "La veranda coperta sulla piscina",
    cls: "md:col-span-7 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "https://a0.muscache.com/im/pictures/9f3db89c-15e5-4694-be00-7ccd6f19522e.jpg?im_w=1440",
    alt: "La piscina privata e la villa",
    cls: "md:col-span-5 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "/photos/garden_4.jpg",
    alt: "La cascata della piscina",
    cls: "md:col-span-7 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "/photos/garden_2.jpg",
    alt: "La piscina con l'idromassaggio",
    cls: "md:col-span-5 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "/photos/pool_3.jpg",
    alt: "Il grande giardino della villa",
    cls: "md:col-span-7 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "https://a0.muscache.com/im/pictures/158222e0-9080-4c26-913b-eeba780875d0.jpg?im_w=1440",
    alt: "Il giardino di palme",
    cls: "md:col-span-5 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "https://a0.muscache.com/im/pictures/hosting/Hosting-26104436/original/30857db8-6aaf-4ce2-8114-d462ffa5747b.jpeg?im_w=1440",
    alt: "Il salone interno",
    cls: "md:col-span-7 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "https://cf.bstatic.com/xdata/images/hotel/max1024x768/844591817.jpg?k=02887bc52aa46ae7a53a0c2e7d60a276a492b15233866f91eb7946bd6d82f3de&o=",
    alt: "Il bagno con vasca",
    cls: "md:col-span-5 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "/photos/inter_3.jpg",
    alt: "La zona barbecue in pietra",
    cls: "md:col-span-7 md:h-[540px]",
    mH: "h-[300px]",
  },
  {
    src: "https://cf.bstatic.com/xdata/images/hotel/max1024x768/841023748.jpg?k=a9cdd486ef729d0e91fbbbb83844f2c2ae559ef102ac377e394e5c6f84cb5138&o=",
    alt: "La camera da letto principale",
    cls: "md:col-span-12 md:h-[520px]",
    mH: "h-[320px]",
  },
];

const MOBILE_H = ["h-[300px]", "h-[300px]", "h-[300px]", "h-[300px]", "h-[300px]", "h-[300px]", "h-[300px]", "h-[300px]", "h-[320px]"];

export default function Gallery() {
  const { t } = useLang();
  const [open, setOpen] = useState(null);
  const [custom, setCustom] = useState([]);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y0 = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const y1 = useTransform(scrollYProgress, [0, 1], [-20, 20]);
  const y2 = useTransform(scrollYProgress, [0, 1], [25, -25]);
  const y3 = useTransform(scrollYProgress, [0, 1], [-25, 25]);
  const y4 = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const y5 = useTransform(scrollYProgress, [0, 1], [-15, 15]);
  const PARALLAX = [y0, y1, y2, y3, y4, y5];

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`)
      .then((r) => r.json())
      .then((d) => setCustom(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const ALL = [
    ...custom.map((c, i) => ({
      src: c.url,
      alt: c.alt || "La villa",
      cls: i % 2 ? "md:col-span-5 md:h-[400px]" : "md:col-span-7 md:h-[400px]",
      mH: "h-[260px]",
    })),
    ...IMAGES,
  ];
  const mobileH = (i) => ALL[i]?.mH || MOBILE_H[Math.min(i, MOBILE_H.length - 1)] || "h-[260px]";

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((i) => (i + 1) % ALL.length);
      if (e.key === "ArrowLeft") setOpen((i) => (i - 1 + ALL.length) % ALL.length);
    };
    window.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [open, ALL.length]);

  return (
    <section id="galleria" ref={ref} className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
      <div className="mb-14 md:mb-20">
        <Reveal>
          <Eyebrow>{t.gallery.eyebrow}</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-5 font-serif text-4xl font-light md:text-6xl">{t.gallery.title}</h2>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
        {ALL.map((img, i) => (
          <motion.div key={img.src.slice(-24) + i} style={{ y: PARALLAX[i] }} className={(img.cls.split(" ")[0] || "").replace(/^$/, "md:col-span-4")}>
            <Reveal delay={0.05 * (i % 6)} y={24}>
              <button
                data-testid={`gallery-image-${i}`}
                onClick={() => setOpen(i)}
                className={`group relative block w-full overflow-hidden rounded-lg ${mobileH(i)} ${img.cls.split(" ")[1] || ""} ${img.cls.split(" ")[2] || ""}`}
                aria-label={img.alt}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-ink/0 transition-colors duration-500 group-hover:bg-ink/20" />
              </button>
            </Reveal>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 backdrop-blur-md"
            onClick={() => setOpen(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setOpen(null)}
              aria-label="Chiudi"
              className="absolute right-5 top-5 z-10 grid h-11 w-11 place-items-center rounded-full border border-sand/30 text-sand transition-colors duration-300 hover:bg-sand hover:text-ink"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((i) => (i - 1 + ALL.length) % ALL.length);
              }}
              aria-label="Precedente"
              className="absolute left-3 z-10 grid h-12 w-12 place-items-center rounded-full border border-sand/30 text-sand transition-colors duration-300 hover:bg-sand hover:text-ink md:left-8"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((i) => (i + 1) % ALL.length);
              }}
              aria-label="Successiva"
              className="absolute right-3 z-10 grid h-12 w-12 place-items-center rounded-full border border-sand/30 text-sand transition-colors duration-300 hover:bg-sand hover:text-ink md:right-8"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            <motion.figure
              key={open}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-[85vh] max-w-[90vw]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={ALL[open].src}
                alt={ALL[open].alt}
                className="max-h-[78vh] w-auto max-w-full rounded-lg object-contain"
              />
              <figcaption className="mt-4 flex items-center justify-between text-sand/70">
                <span className="font-serif text-lg italic">{ALL[open].alt}</span>
                <span className="text-xs tracking-[0.3em]">
                  {open + 1} / {ALL.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
